import { action, computed, makeAutoObservable } from 'mobx';
import { BonusCreationStatusType } from 'src/api_openapi/v2/bonus-engine/schemas';
import { Common } from 'src/AppState/Common';
import { RouterBackoffice } from 'src/AppState/RouterBackoffice';
import { BonusCreationStore } from 'src/domains/casino/state/AppCasino/store/bonus-creation-store/BonusCreation.store';
import { BonusCreationModel } from 'src/domains/casino/state/AppCasino/store/bonus-creation-store/BonusCreationModel';
import {
    FilterMenuItemType,
    FilterStateType,
} from 'src/domains/casino/webview/bonus-engine/campaign-list/filters/filters.type';
import { PaginationConfigType } from 'src/domains/casino/webview/bonus-engine/common/components/pagination-table/PaginationTable';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { DateTime } from 'src_common/utils/time/time';

export const validStatuses: BonusCreationStatusType[] = ['draft', 'published'];
type SortableKeysType = keyof BonusCreationModel | 'rewardName';

const pageSizeOptions: number[] = [10, 25, 50, 100];

interface SelectValueType {
    value: string;
    label: string;
    onClick: () => void;
}

export class BonusCreatorState implements FilterStateType {
    private static instance: BonusCreatorState | null = null;

    public isAllExpanded: boolean = false;

    public selectedRewards: string[] = [];
    public selectedStatuses: string[] = [];

    public expandedRows: string[] = [];
    public filter: FormInputState<string, string> = FormInputState.new('');
    public sortConfig: { key: SortableKeysType; direction: 'ascending' | 'descending' } | null = null;
    public currentPage: number = 1;
    public rowsPerPage: number = 10;
    public rowsPerPageState = FormInputState.new('');

    public constructor(
        private common: Common,
        private readonly routerBackoffice: RouterBackoffice
    ) {
        const route = this.routerBackoffice.getPrevValueString('global');

        if (BonusCreatorState.instance !== null && route === 'bonus-engine') {
            return BonusCreatorState.instance;
        }

        makeAutoObservable(this);
        this.setRowsPerPage(this.rowsPerPage);

        BonusCreatorState.instance = this;
    }

    public get isNoFilterSelected(): boolean {
        return this.selectedRewards.length === 0 && this.selectedStatuses.length === 0;
    }

    @action public setFilter = (value: string): void => {
        this.filter.setValue(value);
        this.currentPage = 1;
    };

    @action public setSortConfig = (key: keyof BonusCreationModel | 'rewardName'): void => {
        if (this.sortConfig !== null && this.sortConfig.key === key) {
            this.sortConfig.direction = this.sortConfig.direction === 'ascending' ? 'descending' : 'ascending';
        } else {
            this.sortConfig = { key, direction: 'ascending' };
        }
    };

    @action public setPage = (page: number): void => {
        this.currentPage = Math.max(1, Math.min(page, this.totalPages));
    };

    @action public toggleRowExpansion = (bonusCreationId: string): void => {
        if (this.expandedRows.includes(bonusCreationId)) {
            this.expandedRows = this.expandedRows.filter((id) => id !== bonusCreationId);
        } else {
            this.expandedRows.push(bonusCreationId);
        }
    };

    @action public expandAllWithDescription = (): void => {
        const bonusCreationStore = BonusCreationStore.get(this.common);
        const bonusCreations = bonusCreationStore.allBonusCreations;

        const bonusCreationsWithDescription = bonusCreations
            .filter((bonusCreation) => bonusCreation.terms)
            .map((bonusCreation) => bonusCreation.id);

        if (this.isAllExpanded) {
            this.expandedRows = this.expandedRows.filter((id) => !bonusCreationsWithDescription.includes(id));
            this.isAllExpanded = false;
        } else {
            this.expandedRows = Array.from(new Set([...this.expandedRows, ...bonusCreationsWithDescription]));
            this.isAllExpanded = true;
        }
    };

    @action public setRowsPerPage = (rows: number): void => {
        this.rowsPerPage = rows;
        this.rowsPerPageState.setValue(rows.toString());
        this.currentPage = 1;
    };

    public get selectValues(): SelectValueType[] {
        return pageSizeOptions.map((size) => ({
            value: size.toString(),
            label: size.toString(),
            onClick: () => this.setRowsPerPage(size),
            dataTest: `page-size-${size}`,
        }));
    }

    private filterBonusCreations(data: Array<BonusCreationModel>): Array<BonusCreationModel> {
        const filterText = this.filter.value.toLowerCase();

        if (filterText !== '') {
            data = data.filter((bonusCreation) => {
                const matchesName = bonusCreation.name.toLowerCase().includes(filterText);
                const matchesDescription = bonusCreation.terms.toLowerCase().includes(filterText);
                return matchesName || matchesDescription;
            });
        }

        if (this.selectedStatuses.length > 0) {
            data = data.filter((bonusCreation) => this.selectedStatuses.includes(bonusCreation.status));
        }

        if (this.selectedRewards.length > 0) {
            data = data.filter((bonusCreation) =>
                bonusCreation.rewardType === null ? false : this.selectedRewards.includes(bonusCreation.rewardType)
            );
        }

        return data;
    }

    private sortBonusCreations(data: Array<BonusCreationModel>): Array<BonusCreationModel> {
        if (this.sortConfig === null) {
            return data;
        }

        const { key, direction } = this.sortConfig;

        return [...data].sort((a, b) => {
            const getValue = (
                bonusCreation: BonusCreationModel,
                key: keyof BonusCreationModel | 'rewardName'
            ): string | number | undefined => {
                if (key === 'rewardName') {
                    return bonusCreation.rewardName;
                }

                const value = bonusCreation[key];

                if (value instanceof DateTime) {
                    return value.unixMs();
                }

                return typeof value === 'string' || typeof value === 'number' ? value : undefined;
            };

            const aValue = getValue(a, key);
            const bValue = getValue(b, key);

            if (typeof aValue === 'string' && typeof bValue === 'string') {
                return direction === 'ascending' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
            } else if (typeof aValue === 'number' && typeof bValue === 'number') {
                return direction === 'ascending' ? aValue - bValue : bValue - aValue;
            }

            return 0;
        });
    }

    @computed public get filteredData(): Array<BonusCreationModel> {
        const bonusCreationStore = BonusCreationStore.get(this.common);
        let data = bonusCreationStore.allBonusCreations;

        data = this.filterBonusCreations(data);

        return this.sortBonusCreations(data);
    }

    @computed public get paginatedData(): Array<BonusCreationModel> {
        const data = this.filteredData;

        const startIndex = (this.currentPage - 1) * this.rowsPerPage;
        return data.slice(startIndex, startIndex + this.rowsPerPage);
    }

    @computed public get totalPages(): number {
        return Math.max(1, Math.ceil(this.filteredData.length / this.rowsPerPage));
    }

    @computed public get totalItems(): number {
        return this.filteredData.length;
    }

    @computed public get startItem(): number {
        if (this.totalItems === 0) {
            return 0;
        }
        return (this.currentPage - 1) * this.rowsPerPage + 1;
    }

    @computed public get endItem(): number {
        return Math.min(this.startItem + this.rowsPerPage - 1, this.totalItems);
    }

    @computed public get paginationConfig(): PaginationConfigType<string> {
        return {
            currentPage: this.currentPage,
            endItem: this.endItem,
            setPage: this.setPage,
            startItem: this.startItem,
            totalItems: this.totalItems,
            totalPages: this.totalPages,
            rowsPerPageState: this.rowsPerPageState,
            selectValues: this.selectValues,
        };
    }

    /*************** Filters **************/
    @computed public get filterMenuItems(): FilterMenuItemType[] {
        return [
            {
                label: 'Reward',
                items: [
                    { key: 'free-bet', label: 'Free Bet' },
                    { key: 'free-spin', label: 'Free Spin' },
                ],
            },
            {
                label: 'Status',
                items: [
                    { key: 'draft', label: 'Draft' },
                    { key: 'published', label: 'Published' },
                ],
            },
        ];
    }

    @action public toggleSelectedReward = (rewardKey: string): void => {
        if (this.selectedRewards.includes(rewardKey)) {
            this.selectedRewards = this.selectedRewards.filter((r) => r !== rewardKey);
        } else {
            this.selectedRewards.push(rewardKey);
        }
        this.setPage(1);
    };

    @action public toggleSelectedStatus = (statusKey: string): void => {
        if (this.selectedStatuses.includes(statusKey)) {
            this.selectedStatuses = this.selectedStatuses.filter((s) => s !== statusKey);
        } else {
            this.selectedStatuses.push(statusKey);
        }
        this.setPage(1);
    };

    @action public clearFilters = (): void => {
        this.selectedRewards = [];
        this.selectedStatuses = [];
        this.setPage(1);
    };
}
